import './Tabs.css';
import React, { useRef, useState, useEffect, Fragment } from 'react';
import { canUseDOM, get, getColorByTheme, getPageThemeColors } from '../../utils';
import { RichText, Text, Link, Image } from '@sitecore-jss/sitecore-jss-react';
import RouterLink from '../atoms/RouterLink';
import LazyPicture from '../atoms/LazyPicture';
import { SitecoreContextReactContext } from '../../utils/SitecoreContextReactContext';
import ComponentsWrapper from '../atoms/ComponentsWrapper';
import { tealiumClickFunc, tealiumMergeClickData } from '../../utils/tealium';
import { ReactComponent as ChevronDetLeftIcon } from '../../resources/icons/chevron-left-16-px.svg';
import { ReactComponent as ChevronDetRightIcon } from '../../resources/icons/chevron-right-16-px.svg';
import debounce from 'lodash/debounce';
import { ReactComponent as ArrowTabIcon } from '../../resources/icons/IconArrowTab.svg';
const Tabs = _ref => {
  let {
    fields,
    params
  } = _ref;
  const sitecoreContext = React.useContext(SitecoreContextReactContext);
  const rtl = Boolean(sitecoreContext && sitecoreContext.rtl);
  const pageEditing = Boolean(sitecoreContext && sitecoreContext.pageEditing);
  const {
    clickCategoryAnalytics
  } = fields.data;
  const [activeTab, setActiveTab] = useState(0);
  const [isVdsite, setIsVdsite] = React.useState(true);
  const [isMobile, setIsMobile] = useState(() => canUseDOM ? window.matchMedia(`(max-width: 767px)`).matches : false);
  const isDetSite = get(params, 'RenderingStyle') === 'DDA' || false;
  const reverseMedia = params !== undefined && params['Left Media'] === '1' ? true : false;
  const checkMobile = () => {
    setIsMobile(window.matchMedia(`(max-width: 767px)`).matches);
  };
  React.useEffect(() => {
    if (canUseDOM && !document.body.classList.contains('vdsite')) {
      setIsVdsite(false);
    }
    if (canUseDOM) {
      window.addEventListener('resize', debounce(checkMobile, 250));
      window.addEventListener('resize', debounce(checkForDisabledButtons, 250));
      checkForDisabledButtons();
      window.addEventListener('touchend', checkForDisabledButtons);
    }
    return () => {
      if (canUseDOM) {
        window.removeEventListener('resize', checkMobile);
      }
      window.removeEventListener('resize', checkForDisabledButtons);
      window.removeEventListener('touchend', checkForDisabledButtons);
    };
  }, []);
  const templateName = get(sitecoreContext, 'route.templateName');
  const primaryPageThemeColor = get(sitecoreContext, 'themesOfPage.primary');
  const primaryActivePageThemeColor = get(sitecoreContext, 'themesOfPage.primary_active');
  const BackgroundColorHex = get(params, 'BackgroundColorHex');
  const ColorHex = get(params, 'ColorHex');
  const ActiveColorHex = get(params, 'ActiveColorHex');
  const selectedPrimaryColor = primaryPageThemeColor ? primaryPageThemeColor : getPageThemeColors(isVdsite).primary;
  const variables = BackgroundColorHex ? {
    '--m49-tabs-primary-color': BackgroundColorHex,
    '--m49-tabs-active-color': ActiveColorHex,
    '--m49-tabs-color': ColorHex
  } : {
    '--m49-tabs-primary-color': selectedPrimaryColor,
    '--m49-tabs-active-color': primaryActivePageThemeColor ? primaryActivePageThemeColor : getPageThemeColors(isVdsite).primary_active,
    '--m49-tabs-color': getColorByTheme(selectedPrimaryColor)
  };
  const switchTab = React.useCallback(e => {
    const index = e.currentTarget.getAttribute('data-tab');
    setActiveTab(parseInt(index, 10));
  }, []);

  // horizontal scroll
  const tabsHorizontalContainerRef = useRef(null);
  const [leftButtonVisibility, setLeftButtonVisibility] = useState(false);
  const [rightButtonVisibility, setRightButtonVisibility] = useState(false);
  const getAvgScrollWidth = childNodes => {
    let acc = 0;
    childNodes.forEach((el, index) => {
      if (childNodes.length - 1 === index) {
        acc = acc / index;
        return acc;
      }
      acc += el.offsetWidth;
      return acc;
    }, 0);
    return Math.round(acc) + 15;
  };
  const checkForDisabledButtons = () => {
    if (isDetSite && isMobile) {
      setTimeout(() => {
        const {
          scrollLeft,
          clientWidth,
          scrollWidth
        } = tabsHorizontalContainerRef.current;
        if (rtl) {
          if (scrollLeft === 0) {
            setLeftButtonVisibility(false); // the start of nav list, hide right button
          } else if (scrollLeft !== 0) {
            setLeftButtonVisibility(true);
          }
          if (parseInt((scrollWidth + parseInt(scrollLeft.toString())).toString()) === clientWidth || parseInt((scrollWidth + parseInt(scrollLeft.toString())).toString()) + 1 === clientWidth || parseInt((scrollWidth + parseInt(scrollLeft.toString())).toString()) - 1 === clientWidth) {
            setRightButtonVisibility(false); // the end of nav list, hide left button
            return;
          } else {
            return setRightButtonVisibility(true);
          }
        }
        if (scrollLeft === 0) {
          setRightButtonVisibility(false); // the start of nav list, hide left button
        } else if (scrollLeft !== 0) {
          setRightButtonVisibility(true);
        }
        if (parseInt((scrollWidth - parseInt(scrollLeft.toString())).toString()) === clientWidth || parseInt((scrollWidth - parseInt(scrollLeft.toString())).toString()) + 1 === clientWidth || parseInt((scrollWidth - parseInt(scrollLeft.toString())).toString()) - 1 === clientWidth) {
          setLeftButtonVisibility(false); // the end of nav list, hide right button
          return;
        } else {
          return setLeftButtonVisibility(true);
        }
      }, 200);
    } else {
      setLeftButtonVisibility(false);
      setRightButtonVisibility(false);
      return false;
    }
  };
  const scroll = scrollOffsetType => {
    const {
      scrollLeft,
      childNodes
    } = tabsHorizontalContainerRef.current;
    const averageScrollValue = getAvgScrollWidth(childNodes);
    if (scrollOffsetType === 'left') {
      tabsHorizontalContainerRef.current.scrollTo({
        left: !rtl ? scrollLeft - averageScrollValue : scrollLeft + averageScrollValue,
        behavior: 'smooth'
      });
    }
    if (scrollOffsetType === 'right') {
      tabsHorizontalContainerRef.current.scrollTo({
        left: !rtl ? scrollLeft + averageScrollValue : scrollLeft - averageScrollValue,
        behavior: 'smooth'
      });
    }
    checkForDisabledButtons();
  };
  useEffect(() => {
    setTimeout(checkForDisabledButtons, 200);
  }, [sitecoreContext.selectedFontSize]);
  if (!fields || !fields.data || !fields.data.item) {
    console.info('Tabs component render skipped. No fields provided');
    if (pageEditing) {
      return /*#__PURE__*/React.createElement("div", null, "[Tabs component is empty: please provide data source]");
    }
    return null;
  }
  const title = get(fields, 'data.item.title.jss');
  const titleValue = String(title ? title.value || '' : '');
  const subTitle = get(fields, 'data.item.subtitle.jss', '');
  const subTitleValue = String(title ? title.value || '' : '');
  const hasItems = Boolean(get(fields, 'data.item.children.length'));
  const items = hasItems ? fields.data.item.children : [];
  const {
    tabAnalytics
  } = fields.data;
  const renderTabs = /*#__PURE__*/React.createElement("div", {
    className: "tabs",
    "data-cy": "tabs",
    style: variables
  }, Boolean(titleValue.trim() || subTitleValue.trim()) && /*#__PURE__*/React.createElement("h3", {
    className: "h3 tabs__title",
    "data-cy": "tabs-title"
  }, /*#__PURE__*/React.createElement(Text, {
    tag: "",
    field: title
  }), " ", /*#__PURE__*/React.createElement("br", null), Boolean(subTitleValue) && /*#__PURE__*/React.createElement("span", {
    className: "tabs__subtitle",
    "data-cy": "tabs-subtitle"
  }, /*#__PURE__*/React.createElement(Text, {
    tag: "",
    field: subTitle
  }))), /*#__PURE__*/React.createElement("div", {
    className: "tabs-group",
    "data-cy": "tabs-group"
  }, /*#__PURE__*/React.createElement("div", {
    className: `tabs-heading col-xs-12 rs_skip ${rightButtonVisibility && 'tabs-heading--right-scroll'} ${leftButtonVisibility && 'tabs-heading--left-scroll'}`
  }, /*#__PURE__*/React.createElement("ul", {
    className: "h__list scroll-no-style",
    ref: tabsHorizontalContainerRef
  }, items.map((item, index) => {
    const itemTitlteField = get(item, 'title.jss', '');
    if (!itemTitlteField.value) {
      return null;
    }
    if (!pageEditing) {
      return /*#__PURE__*/React.createElement("li", {
        key: (itemTitlteField.value + index).toString(),
        className: `h__item ${activeTab === index ? 'active' : ''}`,
        "data-tab": index,
        onClick: switchTab
      }, /*#__PURE__*/React.createElement("a", {
        href: "#",
        onClick: e => {
          e.preventDefault();
          tealiumClickFunc(tealiumMergeClickData(tabAnalytics, {
            eventAct: params.eventAct,
            eventCat: params.eventCat
          }), item.name);
        }
      }, itemTitlteField.value, isDetSite ? rtl ? /*#__PURE__*/React.createElement(ChevronDetLeftIcon, null) : /*#__PURE__*/React.createElement(ChevronDetRightIcon, null) : null));
    } else {
      return /*#__PURE__*/React.createElement("li", {
        key: (itemTitlteField.value + index).toString(),
        className: "h__item"
      }, /*#__PURE__*/React.createElement(Text, {
        tag: "div",
        field: itemTitlteField
      }));
    }
  })), isDetSite && rightButtonVisibility && isMobile ? /*#__PURE__*/React.createElement("button", {
    onClick: () => {
      scroll(!rtl ? 'left' : 'right');
    },
    className: "tabs-heading__nav-button tabs-heading__nav-button--left"
  }, /*#__PURE__*/React.createElement(ArrowTabIcon, null)) : null, isDetSite && leftButtonVisibility && isMobile ? /*#__PURE__*/React.createElement("button", {
    onClick: () => {
      scroll(!rtl ? 'right' : 'left');
    },
    className: "tabs-heading__nav-button tabs-heading__nav-button--right"
  }, /*#__PURE__*/React.createElement(ArrowTabIcon, null)) : null), /*#__PURE__*/React.createElement("div", {
    className: "tabs-body col-xs-12",
    "data-cy": "tabs-body"
  }, items.map((item, index) => {
    const hasCtaLabel = Boolean(get(item, 'ctaLabel.jss.value'));
    const ctaLabel = get(item, 'ctaLabel.jss.value');
    const ctaAnalytics = get(item, 'ctaAnalytics.jss.value', ctaLabel);
    const ctaLinkHref = get(item, 'ctaLink.jss.value.href', '');
    const desc = get(item, 'description.jss');
    const itemTitlte = get(item, 'title.jss.value', '');
    const ctaLeftIcon = get(item, 'ctaLeftIcon.targetItem.svg.jss.value');
    const ctaRightIcon = get(item, 'ctaRightIcon.targetItem.svg.jss.value');
    if (!itemTitlte) {
      return null;
    }
    return /*#__PURE__*/React.createElement(Fragment, {
      key: (itemTitlte + index).toString()
    }, /*#__PURE__*/React.createElement("h3", {
      className: pageEditing ? '' : 'd-none'
    }, itemTitlte), /*#__PURE__*/React.createElement("div", {
      className: `tabs-content ${activeTab === index ? 'active' : ''} ${pageEditing ? 'active' : ''} ${item.image && item.image.jss.value.src ? 'tabs-content--has-image' : null} ${reverseMedia && item.image && item.image.jss.value.src ? 'tabs-content--reverse-media' : 'tabs-content--default'}`,
      style: pageEditing ? {
        marginBottom: '40px'
      } : {}
    }, /*#__PURE__*/React.createElement("div", {
      className: "c-bloc__txt"
    }, /*#__PURE__*/React.createElement(RichText, {
      className: "c__txt",
      tag: "div",
      field: desc
    }), isMobile && item.image && item.image.jss.value.src && /*#__PURE__*/React.createElement(React.Fragment, null, !pageEditing ? /*#__PURE__*/React.createElement(LazyPicture, {
      className: "c__img"
    }, /*#__PURE__*/React.createElement("source", {
      media: "(min-width: 440px)",
      srcSet: `${item.image.jss.value.src}`
    }), /*#__PURE__*/React.createElement("img", {
      "data-cy": "tabs-body-image",
      src: `${item.image.jss.value.src}&cw=430&ch=430`,
      alt: item.image.jss.value.alt
    })) : /*#__PURE__*/React.createElement(Image, {
      editable: true,
      field: item.image.jss
    })), hasCtaLabel && ctaLinkHref && (pageEditing ? /*#__PURE__*/React.createElement(Link, {
      className: "btn btn-primary tab-btn",
      field: item.ctaLink.jss
    }) : /*#__PURE__*/React.createElement(RouterLink, {
      className: "btn btn-primary tab-btn",
      href: ctaLinkHref,
      dataCy: "tabs-body-cta",
      linktype: item.ctaLink.jss.value.linktype,
      ctaLeftIcon: ctaLeftIcon,
      ctaRightIcon: ctaRightIcon,
      isDetSite: isDetSite,
      title: `${itemTitlte} | ${item.ctaLabel.jss.value}`,
      onClick: () => {
        tealiumClickFunc(tealiumMergeClickData(clickCategoryAnalytics, {
          eventAct: params.eventAct,
          eventCat: params.eventCat
        }), [item.name, ctaAnalytics]);
      }
    }, item.ctaLabel.jss.value))), !isMobile && item.image && item.image.jss.value.src && /*#__PURE__*/React.createElement(React.Fragment, null, !pageEditing ? /*#__PURE__*/React.createElement(LazyPicture, {
      className: "c__img",
      style: {
        flexShrink: 0
      }
    }, /*#__PURE__*/React.createElement("source", {
      media: "(min-width: 991px)",
      srcSet: `${item.image.jss.value.src}&cw=460&ch=345`
    }), /*#__PURE__*/React.createElement("img", {
      "data-cy": "tabs-body-image",
      src: `${item.image.jss.value.src}&cw=300&ch=345`,
      alt: item.image.jss.value.alt
    })) : /*#__PURE__*/React.createElement(Image, {
      editable: true,
      field: item.image.jss
    }))));
  }))));
  if (templateName === 'Weather Information Page') {
    return /*#__PURE__*/React.createElement(StyledWrapper, {
      rtl: rtl,
      anchorUrlParameter: params['Anchor Url Parameter'],
      isDetSite: isDetSite
    }, /*#__PURE__*/React.createElement("div", {
      className: "row"
    }, /*#__PURE__*/React.createElement("div", {
      className: "col-md-1"
    }), /*#__PURE__*/React.createElement("div", {
      className: "col-md-10"
    }, renderTabs), /*#__PURE__*/React.createElement("div", {
      className: "col-md-1"
    })));
  }
  return /*#__PURE__*/React.createElement(StyledWrapper, {
    rtl: rtl,
    anchorUrlParameter: params['Anchor Url Parameter'],
    isDetSite: isDetSite
  }, renderTabs);
};
const StyledWrapper = _ref2 => {
  let {
    children,
    rtl,
    anchorUrlParameter,
    isDetSite
  } = _ref2;
  const commonStyles = isDetSite ? "ccw3y7n" : "cw99v5t";
  const rtlStyles = isDetSite ? "r1da4qmh" : "rr8vzo2";
  const ltrStyles = isDetSite ? "l1w62c6" : "lti9ocp";
  return /*#__PURE__*/React.createElement(ComponentsWrapper, {
    anchorUrlParameter: anchorUrlParameter,
    minHeight: true,
    className: `${commonStyles} ${rtl ? rtlStyles : ltrStyles}`
  }, children);
};
export default Tabs;